import { type FC, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea.tsx';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';
import { FormattedMessage } from 'dibs-react-intl';
import { SeoLink } from 'dibs-seo/exports/SeoLink.tsx';
import { trackEvent } from 'dibs-tracking';

import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { fireReadMoreTracking } from '../PdpSharedStorytellingComponents/pdpSharedStorytellingHelpers.ts';
import { PdpSharedStorytellingShopAllLink } from '../PdpSharedStorytellingComponents/PdpSharedStorytellingShopAllLink.tsx';
import {
    pdpAccordionReadLessLabel,
    pdpAccordionReadMoreButtonClass,
    pdpAccordionReadMoreLabel,
    pdpAccordionReadMoreWrapperClass,
} from './PdpRespAccordionReadMoreLabels.tsx';

import { type PdpRespCreatorsPanelCreator_storyTellingTab$key } from './__generated__/PdpRespCreatorsPanelCreator_storyTellingTab.graphql.ts';

import styles from './PdpRespCreatorsPanelCreator.module.css';

const CREATOR_IMAGE_WIDTH = 180;

const storyTellingTabFragment = graphql`
    fragment PdpRespCreatorsPanelCreator_storyTellingTab on StoryTellingTab {
        tabType
        contentHeader
        contentCreatorAttribution {
            creatorName
            creatorBrowseUrl {
                ...SeoLink_linkData
            }
        }
        contentBodyText
        smallImage
        mainImage
        ...PdpSharedStorytellingShopAllLink_storyTellingTab
    }
`;

type Props = {
    storyTellingTab: PdpRespCreatorsPanelCreator_storyTellingTab$key;
    reserveImageColumn?: boolean;
};

export const PdpRespCreatorsPanelCreator: FC<Props> = ({
    storyTellingTab: storyTellingTabRef,
    reserveImageColumn = false,
}) => {
    const storyTellingTab = useFragment(storyTellingTabFragment, storyTellingTabRef);
    const { isDesktop } = useServerVarsContext();

    const {
        tabType,
        contentHeader,
        contentCreatorAttribution,
        contentBodyText,
        smallImage,
        mainImage,
    } = storyTellingTab;
    const imageSrc = smallImage || mainImage;
    // Even though the image size is fixed to 180x180, we still provide a higher image resolution for Retina/2x screens which has 360 physical pixels.
    const imageSrcSet = imageSrc
        ? getSrcsetString(imageSrc, [CREATOR_IMAGE_WIDTH, CREATOR_IMAGE_WIDTH * 2])
        : undefined;
    const imageAlt = contentHeader?.replace(/<[^>]*>/g, '') ?? '';

    const onReadMoreClick = useCallback(() => {
        fireReadMoreTracking({ tabType });
    }, [tabType]);

    if (!contentHeader && !contentBodyText) {
        return null;
    }

    return (
        <div
            className={classnames(
                dibsCss.flex,
                dibsCss.wFull,
                dibsCss.itemsStart,
                isDesktop ? dibsCss.flexRow : dibsCss.flexCol,
                isDesktop ? dibsCss.gapLarge : dibsCss.gapSmall
            )}
            data-tn="pdp-creators-panel-creator"
        >
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.flexCol,
                    dibsCss.gapSmall,
                    dibsCss.flex1,
                    dibsCss.minW0,
                    dibsCss.minH0,
                    dibsCss.wFull
                )}
            >
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.gapXsmall,
                        dibsCss.wFull
                    )}
                >
                    {contentHeader && (
                        <div
                            className={classnames(
                                dibsCss.textNoir,
                                dibsCss.sassyFontHeaderLarge,
                                dibsCss.wFull
                            )}
                            data-tn="pdp-storytelling-creator-name"
                            dangerouslySetInnerHTML={{ __html: contentHeader }}
                        />
                    )}
                    {contentCreatorAttribution && (
                        <div className={dibsCss.sassyFontBodyMedium}>
                            <FormattedMessage
                                defaultMessage="Designed by {creator}"
                                id="pdp.resp.storytelling.creatorAttribution"
                                values={{
                                    creator: (
                                        <SeoLink
                                            linkData={contentCreatorAttribution.creatorBrowseUrl}
                                            dataTn="pdp-storytelling-design-attribution-link"
                                            onClick={() => {
                                                trackEvent({
                                                    category: 'promo interaction',
                                                    action: 'iconic design creator clicked',
                                                    label: 'pdp',
                                                });
                                            }}
                                        >
                                            {contentCreatorAttribution.creatorName}
                                        </SeoLink>
                                    ),
                                }}
                            />
                        </div>
                    )}
                    <div className={dibsCss.sassyFontBodyMediumHeavy}>
                        <PdpSharedStorytellingShopAllLink
                            storyTellingTab={storyTellingTab}
                            buyerLinkType="standardLink"
                        />
                    </div>
                </div>
                {contentBodyText && (
                    <ExpandingAreaWithReadMore
                        readMore={pdpAccordionReadMoreLabel}
                        readLess={pdpAccordionReadLessLabel}
                        collapsedHeight={168}
                        lineClamp={7}
                        showGradient={false}
                        disableScrollOnCollapse
                        onReadMoreClick={onReadMoreClick}
                        wrapperClass={pdpAccordionReadMoreWrapperClass}
                        buttonClass={pdpAccordionReadMoreButtonClass}
                        buttonUnderline="none"
                    >
                        <span
                            className={classnames(
                                dibsCss.block,
                                dibsCss.whitespaceNormal,
                                dibsCss.spaceYXxsmall,
                                dibsCss.sassyFontBodyMedium,
                                dibsCss.textNoir,
                                dibsCss.wFull
                            )}
                            data-tn="pdp-creators-panel-creator-body"
                            dangerouslySetInnerHTML={{ __html: contentBodyText }}
                        />
                    </ExpandingAreaWithReadMore>
                )}
            </div>
            {(imageSrc || (isDesktop && reserveImageColumn)) && (
                <div
                    className={classnames(
                        styles.imageContainer,
                        dibsCss.flex,
                        dibsCss.itemsCenter,
                        dibsCss.justifyCenter,
                        imageSrc && dibsCss.bgMoonstone,
                        imageSrc && dibsCss.roundedMedium,
                        imageSrc && dibsCss.overflowHidden,
                        dibsCss.flexShrink0
                    )}
                    data-tn={imageSrc ? 'pdp-creators-panel-creator-image' : undefined}
                    aria-hidden={imageSrc ? undefined : true}
                >
                    {imageSrc && (
                        <img
                            className={classnames(
                                styles.image,
                                dibsCss.boxBorder,
                                dibsCss.objectContain,
                                dibsCss.wFull,
                                dibsCss.hFull,
                                dibsCss.pSmall
                            )}
                            src={imageSrc}
                            srcSet={imageSrcSet}
                            sizes={`${CREATOR_IMAGE_WIDTH}px`}
                            alt={imageAlt}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
